import PropTypes from 'prop-types';
import { memo, useState, useLayoutEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import sanitizeHtml from 'sanitize-html';

import { Badge, Text, useTheme } from '@Sage/hoi-poi-ui';
import { BillboardActions, ModalActions } from 'actions';
import { ButtonPrimaryBig } from 'components/buttons';
import { UserChip } from 'containers/components/chips';
import Scrollbars from 'components/ScrollBar';
import FileList from 'containers/components/FileList';
import { getLiteral } from 'utils/getLiteral';
import { successToast } from 'utils/toast';
import './styles.scss';

const propTypes = {
    model: PropTypes.object.isRequired,
};

const PanelReader = memo(({ model }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { loggedUser, literals } = useSelector((state) => ({
        loggedUser: state.config.userData.idUsuario,
        literals: state.literal.literals,
    }));

    const infoRef = useRef(null);
    const actionsRef = useRef(null);
    const [contentOffsetHeight, setContentOffsetHeight] = useState(0);

    useLayoutEffect(() => {
        const observer = new ResizeObserver(() => {
            const infoH = infoRef.current ? infoRef.current.offsetHeight : 0;
            const actionsH = actionsRef.current ? actionsRef.current.offsetHeight : 0;
            setContentOffsetHeight(infoH + actionsH);
        });
        if (infoRef.current) observer.observe(infoRef.current);
        if (actionsRef.current) observer.observe(actionsRef.current);
        return () => observer.disconnect();
    }, []);

    const confirmBillboard = useCallback(
        (model) => {
            const { billboardActionConfirmed } = bindActionCreators(BillboardActions, dispatch);
            billboardActionConfirmed &&
                billboardActionConfirmed(model, () => {
                    successToast({ text: getLiteral('label_billboard_notif_confirmed') });
                });
        },
        [dispatch],
    );

    const onConfirmBillboard = useCallback(() => {
        const { openModal } = bindActionCreators(ModalActions, dispatch);
        const literalAskToConfirm = literals.hasOwnProperty('label_billboard_modal_confirm')
            ? literals['label_billboard_modal_confirm'].replace(
                '%@',
                model.username ? model.username : '-',
            )
            : 'label_billboard_modal_confirm';

        openModal &&
            openModal(
                () => { },
                () => confirmBillboard(model),
                getLiteral('title_warning'),
                literalAskToConfirm,
            );
    }, [dispatch, literals, model, confirmBillboard]);

    let readerStateLiteral =
        model.userHasConfirmed && model.userHasConfirmed(loggedUser)
            ? 'label_billboard_status_confirmed'
            : 'label_billboard_status_pending';
    let files = model.attachmentFiles;

    let literalForConfirm =
        literals && literals.hasOwnProperty('label_billboard_confirm_meaning')
            ? literals['label_billboard_confirm_meaning']
            : 'label_billboard_confirm_meaning';
    if (literalForConfirm.indexOf('%@') !== -1) {
        literalForConfirm = literalForConfirm.replace('%@', model.username);
    }

    let literalForConfirmed =
        literals && literals.hasOwnProperty('label_billboard_confirmed_date')
            ? literals['label_billboard_confirmed_date']
            : 'label_billboard_confirmed_date';
    if (literalForConfirmed.indexOf('%@') !== -1 && model.getTimeConfirmed) {
        literalForConfirmed = literalForConfirmed.replace('%@', model.getTimeConfirmed(loggedUser));
    }

    return (
        <div className="billboard-panel-reader">
            <div className="reader-info" ref={infoRef}>
                <div className="reader-main">
                    <div className="reader-from-box">
                        <Text
                            type="caption"
                            className="info-label"
                            color={theme.colors.utility.textSecondary}
                        >
                            {getLiteral('label_from')}
                        </Text>
                        <UserChip
                            idUser={model.idUser}
                            username={model.username}
                            remotePopover={true}
                        />
                    </div>
                    <Text
                        type="h6"
                        bold
                        as="div"
                        className="reader-title"
                        color={theme.colors.utility.textPrimary}
                    >
                        {model.subject}
                    </Text>
                    <Text
                        type="body"
                        as="div"
                        className="reader-startdate"
                        color={theme.colors.utility.textSecondary}
                    >
                        {model.startDateNotification}
                    </Text>
                    <Badge type="semanticInfo" variant="inverted" className="reader-state">
                        {getLiteral(readerStateLiteral)}
                    </Badge>
                </div>
            </div>
            <div
                className="reader-content"
                style={{ height: `calc(100% - ${contentOffsetHeight}px)` }}
            >
                <Scrollbars autoHide={true}>
                    <FileList files={files} />
                    <div
                        className="ql-editor reader-description"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(model.description) }}
                    />
                </Scrollbars>
            </div>
            <div className="reader-info-actions" ref={actionsRef}>
                {!model?.userHasConfirmed?.(loggedUser) ? (
                    <div className="reader-confirm">
                        <Text
                            type="body"
                            className="reader-confirm-literal"
                            color={theme.colors.utility.textSecondary}
                        >
                            {literalForConfirm}
                        </Text>
                        <ButtonPrimaryBig
                            className="reader-confirm-button"
                            onClick={onConfirmBillboard}
                        >
                            {getLiteral('action_billboard_confirm')}
                        </ButtonPrimaryBig>
                    </div>
                ) : (
                    <Text
                        type="body"
                        as="div"
                        className="time-confirmed-text"
                        color={theme.colors.utility.textSecondary}
                    >
                        {literalForConfirmed}
                    </Text>
                )}
            </div>
        </div>
    );
});

PanelReader.propTypes = propTypes;

export default PanelReader;
